import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import useAxios from "../../utils/useAxios";
import AuthContext from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { Container } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import TournamentMenuList from "../../components/TournamentMenuList";
import FeatherIcon from "feather-icons-react";
import { useHistory, useLocation } from "react-router-dom";
import { COUNTRY_OPTIONS, getGradeName, sports, grades, genders, getInitials } from "../../constants";
import useViewMode from "../../hooks/useViewMode";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import PreImage from "../../components/PreImage";
import { toast } from "react-toastify";
import { getOptionName } from "../../utils/getOptionName";
import TournamentSearch from "./TournamentSearch";
import IconDropdown from "../../components/IconDropdown";
import { prefetchUrl } from "../../utils/tabPrefetch";
import useCompactToolbar from "../../hooks/useCompactToolbar";
import { useTheme } from "../../contexts/ThemeContext";
import { CompactTableRow } from "../../components/CompactTableRow";
import { parseDate } from "../../utils/parseDate";
import { tournamentSubscribers } from "../../utils/tournamentCache";
import { patchedTournamentFields } from "../../utils/patchTournamentFields";


// start_date/end_date carry an actual time of day (the info form uses
// datetime-local for both - a same-day 18:00-21:00 tournament isn't "live"
// at 9am just because it's the right calendar day), so this compares the
// real timestamps with no clamping to day boundaries.
const getTournamentStatus = (tournament) => {
    if (!tournament.start_date || !tournament.end_date) return "upcoming";
    const now = new Date();
    const start = parseDate(tournament.start_date);
    if (!start) return "upcoming";
    const end = parseDate(tournament.end_date);
    if (!end) return "upcoming";
    if (now < start) return "upcoming";
    if (now > end) return "completed";
    return "live";
};

const isSameCalendarDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Shared with the always-visible date pill (not just the status row inside
// the collapsed "details" block) so a live/completed tournament is visible
// at a glance without expanding each card.
const getStatusClass = (tournament) => {
    const status = getTournamentStatus(tournament);
    if (status === "live") return "in-progress";
    if (status === "completed") return "completed";
    // "upcoming" but starts later today - called out separately (orange)
    // so it doesn't blend in with tournaments that are days/weeks away.
    const start = parseDate(tournament.start_date);
    if (start && isSameCalendarDay(start, new Date())) return "today";
    return "expected";
};

const TournamentsList = ({ is_authorized = true }) => {
    const { user } = useContext(AuthContext);
    // scoped per user (so switching accounts on the same browser doesn't
    // inherit another account's leftover filters/date range and make the
    // list look empty) AND per mode (is_authorized) - an admin's own
    // /admin/tournaments and the /tournaments Public View are two different
    // API scopes (own tournaments vs everyone's, see the `api`/`all=true`
    // split below) sharing this same component. Without the mode suffix,
    // switching between them (same logged-in user, usually the same empty
    // query string) silently rehydrated one mode's cached list into the
    // other - e.g. Public View opening already "populated" with whatever
    // the admin had last loaded under /admin/tournaments, since the cache
    // key and its own query-string-only match check couldn't tell the two
    // apart.
    const storageKey = `tournamentsState_${user?.user_id || "public"}_${is_authorized ? "admin" : "public"}`;

    // /admin/tournaments normally omits `all=true` on purpose (see the
    // `all`-param split below) so the backend scopes the list down to just
    // this admin's own club(s) - but that same scoping backend-side still
    // isn't superuser-aware (confirmed directly against the API: a
    // superuser's /tournaments/<id>/ comes back can_edit:false/
    // can_write_in_base:false for a tournament outside their own clubs, and
    // the unscoped list silently drops it too). Forcing `all=true` for a
    // superuser is the only way the admin list actually shows every
    // tournament, not just their own - Tournament.js's own isSuperAdmin
    // override (canWrite/canEdit) is what then lets them act on the ones
    // that aren't theirs once opened.
    const isSuperAdmin = !!user?.is_superuser;
    const savedState = useMemo(() => JSON.parse(sessionStorage.getItem(storageKey)) || {}, [storageKey]);
    const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);

    // navigating into a tournament caches the already-loaded cards + pagination
    // cursor (see the card onClick handlers below), keyed to the exact URL
    // query the list was showing. Coming back with the same query rehydrates
    // from that cache instead of re-fetching page by page - no visible
    // "reloading" while paging in results to reach the one we clicked.
    // Gated on tournamentsScrollTarget (set right before that same
    // navigation, read again below as restoreTargetId) being present - this
    // cache used to rehydrate on ANY mount matching the query string within
    // its 10min TTL, not just a genuine "coming back" navigation. A plain
    // fresh visit/reload of /tournaments has the exact same (empty) query
    // string as one taken minutes earlier, so it could silently inherit
    // whatever was cached from that earlier visit instead of a real fetch.
    const listCacheKey = `tournamentsListCache_${user?.user_id || "public"}_${is_authorized ? "admin" : "public"}`;
    const CACHE_MAX_AGE_MS = 10 * 60 * 1000;
    const [initialCache] = useState(() => {
        try {
            if (!sessionStorage.getItem("tournamentsScrollTarget")) return null;
            const raw = sessionStorage.getItem(listCacheKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (parsed.query !== window.location.search) return null;
            if (!parsed.savedAt || Date.now() - parsed.savedAt > CACHE_MAX_AGE_MS) return null;
            return parsed;
        } catch {
            return null;
        }
    });

    const [tournaments, setTournaments] = useState(() => initialCache?.tournaments || []);

    // App.js keeps this list mounted in the background (just hidden,
    // display:none) while a tournament detail overlay is open on top of it -
    // going back doesn't remount it, so patchTournamentListCache's own
    // sessionStorage patch (read only on a fresh mount) never reaches this
    // already-live `tournaments` state. Without this, editing a tournament's
    // date/name/etc and going back showed the pre-edit card - still grouped
    // under its old day, still showing the old date_range pill - until
    // something else eventually forced a refetch. Subscribing to the same
    // tournamentSubscribers bus Header.js already uses keeps this card in
    // sync the moment Tournament.js's own updateTournament publishes a save.
    useEffect(() => {
        const handleTournamentUpdate = (id, next) => {
            setTournaments(prev => {
                let changed = false;
                const updated = prev.map(t => {
                    if (t?.id !== id) return t;
                    const patch = patchedTournamentFields(t, next);
                    if (!patch) return t;
                    changed = true;
                    return { ...t, ...patch };
                });
                return changed ? updated : prev;
            });
        };
        tournamentSubscribers.add(handleTournamentUpdate);
        return () => tournamentSubscribers.delete(handleTournamentUpdate);
    }, []);
    // "Past" cursor - unchanged in spirit from before, except its own query
    // now stops at "this week" instead of the real end of the month (see
    // changeCurrentPage) - this is the one the bottom sentinel below drives.
    const [currentPage, setCurrentPage] = useState(() => initialCache ? initialCache.currentPage : "");
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(() => initialCache ? initialCache.skeletonCards : 24);
    const [hasError, setHasError] = useState(false);
    // "Future" cursor - the weeks after "this week" within the browsed
    // month, held back from the initial fetch (see changeCurrentPage) and
    // only paged in on an actual upward scroll gesture, or as a fallback
    // when the past side comes up empty/exhausted (see the effects below).
    // null = nothing more above to load (or not applicable: searching, or
    // browsing a month that doesn't contain today).
    const [futureCursor, setFutureCursor] = useState(() => initialCache ? (initialCache.futureCursor ?? null) : null);
    const [loadingFuture, setLoadingFuture] = useState(false);

    const { t, i18n } = useTranslation();

    const [activeChips, setActiveChips] = useState(() => {
        const initialChips = [];

        queryParams.getAll('sport').forEach(val => {
            const obj = sports.find(s => s.id == parseInt(val));
            if(obj) initialChips.push({value: `sport_${val}`, label: t(obj.name), type: 'sport', realValue: val});
        });
        queryParams.getAll('grade').forEach(val => {
            const obj = grades.find(g => g.id == parseInt(val));
            if(obj) initialChips.push({value: `grade_${val}`, label: t(obj.name), type: 'grade', realValue: val});
        });
        queryParams.getAll('gender').forEach(val => {
            const obj = genders.find(g => g.id == parseInt(val));
            if(obj) initialChips.push({value: `gender_${val}`, label: t(obj.name), type: 'gender', realValue: val});
        });
        queryParams.getAll('country').forEach(val => {
            const obj = COUNTRY_OPTIONS.find(c => c.id == val);
            if(obj) initialChips.push({value: `country_${val}`, label: obj.name, type: 'country', realValue: val});
        });
        queryParams.getAll('club').forEach(val => {
            const savedChip = savedState.activeChips?.find(c => c.type === 'club' && String(c.realValue) === String(val));
            initialChips.push({value: `club_${val}`, label: savedChip ? savedChip.label : val, type: 'club', realValue: val});
        });
        queryParams.getAll('location').forEach(val => {
            const savedChip = savedState.activeChips?.find(c => c.type === 'location' && String(c.realValue) === String(val));
            initialChips.push({value: `location_${val}`, label: savedChip ? savedChip.label : val, type: 'location', realValue: val});
        });
        queryParams.getAll('player').forEach(val => {
            const savedChip = savedState.activeChips?.find(c => c.type === 'player' && String(c.realValue) === String(val));
            initialChips.push({value: `player_${val}`, label: savedChip ? savedChip.label : val, type: 'player', realValue: val});
        });
        queryParams.getAll('q').forEach(val => {
            initialChips.push({value: `search_${val}`, label: `🔍 ${val}`, type: 'q', realValue: val});
        });

        if (initialChips.length > 0) return initialChips;
        return savedState.activeChips || [];
    });

    const [isSearching, setIsSearching] = useState(savedState.isSearching || false);
    const [startDate, setStartDate] = useState(
        queryParams.get("start_date") || savedState.startDate || (() => {
            const d = new Date();
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            return `${y}-${m}-01`;
        })()
    );
    const [endDate, setEndDate] = useState(
        queryParams.get("end_date") || savedState.endDate || (() => {
            const d = new Date();
            const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
            const y = lastDay.getFullYear();
            const m = String(lastDay.getMonth() + 1).padStart(2, '0');
            const dd = String(lastDay.getDate()).padStart(2, '0');
            return `${y}-${m}-${dd}`;
        })()
    );

    // see ClubsList.js for why this isn't just useAxios(): its token would
    // still identify a logged-in admin even while viewing the public
    // "Перегляд" list, scoping results to their own tournaments instead of
    // everyone's.
    const authedApi = useAxios();
    const api = is_authorized ? authedApi : axios;
    const history = useHistory();
    const location = useLocation();
    const { viewMode, toggleViewMode } = useViewMode("tournaments_view_mode", "cards");
    // Compact Table is a user-facing-only density preference now - an admin
    // browsing their own /admin/tournaments still gets the regular card
    // view regardless of the stored setting, and only sees it once they
    // switch to Public View (is_authorized false) for this same list. See
    // Header.js/BottomNav.js for the matching toggle-visibility gate.
    const { compactTable: rawCompactTable } = useTheme();
    const compactTable = rawCompactTable && !is_authorized;
    const { ref, inView } = useInView({ threshold: 0, rootMargin: '200px' });
    const [clubs, setClubs] = useState([]);
    // /api/v1.0/tournaments/ only returns each tournament's club NAME, not
    // its id (see the clubIdByName effect below), so the card's club link
    // can't reuse that id the way TournamentHeader's openClubModal does -
    // this resolves name -> id lazily per unique name via the clubs search
    // endpoint instead. Until a name resolves, the card just shows plain
    // text rather than a broken/unclickable link.
    const [clubIdByName, setClubIdByName] = useState({});
    const clubIdFetchingRef = useRef(new Set());
    const searchInputRef = useRef(null);
    // Tracked outside React state (no re-render needed on every keystroke) so
    // the blur handler below can tell "nothing selected AND nothing typed"
    // apart from "nothing selected but mid-typing" - collapsing the toolbar
    // in the latter case was hiding the search row (and the text still
    // sitting in it) the moment focus left the field, before the user had a
    // chance to turn it into a chip.
    const searchQueryRef = useRef("");
    // Mirrors activeChips, but updated synchronously (not through React's
    // render/commit cycle). react-select blurs its own input right after
    // selecting an option on touch devices (blurInputOnSelect) - onChange
    // and onBlur both fire within the same tick, before React has re-
    // rendered with the freshly-added chip, so an onBlur handler reading
    // `activeChips` from its render closure still sees the pre-selection
    // (empty) array and collapses the toolbar out from under the chip that
    // was just added. Updating this ref inside the same onChange call that
    // sets state keeps it correct at the exact moment onBlur reads it.
    const activeChipsRef = useRef(activeChips);
    useEffect(() => { activeChipsRef.current = activeChips; }, [activeChips]);

    // Grid view (a dense multi-column table) doesn't fit a phone-width
    // screen - below the sitewide tablet breakpoint (768px, same one every
    // other mobile-only layout rule in index.css uses) there's no toggle
    // and no grid, only Card View, regardless of a "grid" preference saved
    // from a previous desktop session. Reactive (not a one-off innerWidth
    // check) so rotating a tablet or resizing a desktop window across the
    // breakpoint updates it live instead of only on next full page load.
    const [isMobileWidth, setIsMobileWidth] = useState(() => window.innerWidth < 768);
    useEffect(() => {
        const check = () => setIsMobileWidth(window.innerWidth < 768);
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);
    const effectiveViewMode = isMobileWidth ? "cards" : viewMode;

    const { toolbarSentinelRef, progress: toolbarProgress, activated: toolbarActivated, collapse: collapseToolbar } = useCompactToolbar({
        // instant, not smooth: this page's own infinite-scroll sentinel
        // (the `ref`/`inView` pair above) reacts to layout changes mid-
        // animation and was fighting/cancelling the browser's smooth-scroll
        // partway through, leaving the page stuck below the top.
        onExpand: () => {
            window.scrollTo(0, 0);
            searchInputRef.current?.focus();
        },
        onCreate: is_authorized ? (e) => handleCreateTournament(e) : null
    });

    useEffect(() => {
        const state = { isSearching, activeChips, startDate, endDate };
        sessionStorage.setItem(storageKey, JSON.stringify(state));

        // This list stays mounted in the background while a tournament (or
        // other) detail overlay is open on top of it (App.js's
        // background-location routing keeps it alive so reopening the
        // overlay doesn't remount it) - window.location.pathname then
        // belongs to that overlay, not this list, and writing this list's
        // own filter params over it here was silently stripping the
        // overlay's own query params (e.g. a tournament's ?tab=/admin=)
        // out of the real address bar, surviving invisibly until the next
        // page reload picked up the now-corrupted URL.
        if (window.location.pathname !== "/tournaments" && window.location.pathname !== "/admin/tournaments") {
            return;
        }

        const params = new URLSearchParams();
        if (startDate) params.set("start_date", startDate);
        if (endDate) params.set("end_date", endDate);

        activeChips.forEach(chip => {
            params.append(chip.type, chip.realValue);
        });

        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    }, [startDate, endDate, activeChips]);

    useEffect(() => {
        if (is_authorized) {
            api.get("/api/v1.0/clubs/").then(response => {
                const fetched = response.data.results || response.data || [];
                setClubs(Array.isArray(fetched) ? fetched : []);
            }).catch(error => {
                console.error(error);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [is_authorized]);

    // Resolves each card's plain-text club name to an id, one request per
    // unique name not already cached/in-flight - see clubIdByName's own
    // comment above for why this exists instead of just reading an id off
    // the tournament. Runs off `tournaments` (not currentPage) so newly
    // paged-in cards get their own names picked up too.
    useEffect(() => {
        const names = Array.from(new Set(tournaments.map(t => t.club).filter(Boolean)));
        const toFetch = names.filter(name => !(name in clubIdByName) && !clubIdFetchingRef.current.has(name));
        if (toFetch.length === 0) return;
        toFetch.forEach(name => clubIdFetchingRef.current.add(name));

        Promise.all(toFetch.map(async name => {
            try {
                const params = new URLSearchParams();
                params.set("search", name);
                params.set("limit", "5");
                if (!is_authorized || isSuperAdmin) params.set("all", "true");
                const response = await api.get(`/api/v1.0/clubs/?${params.toString()}`);
                const results = response.data.results || response.data || [];
                const match = Array.isArray(results) ? results.find(c => c.name === name) : null;
                return [name, match ? match.id : null];
            } catch (error) {
                console.error(error);
                return [name, null];
            } finally {
                clubIdFetchingRef.current.delete(name);
            }
        })).then(resolved => {
            setClubIdByName(prev => {
                const next = { ...prev };
                resolved.forEach(([name, id]) => { next[name] = id; });
                return next;
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tournaments, is_authorized]);

    // Same "swap club_id into the current query string" approach as
    // TournamentHeader.js's own openClubModal (ClubProfileModal, mounted
    // globally in App.js, picks it up regardless of which page set it) -
    // replace, not push, so the modal's own close button (and the browser
    // back button) lands back on this list instead of an extra history
    // entry.
    const openClubModal = (e, clubId) => {
        e.preventDefault();
        e.stopPropagation();
        const params = new URLSearchParams(location.search);
        params.set("club_id", clubId);
        history.replace({ search: params.toString() });
    };

    // when returning from a tournament profile, wait until we've either
    // hydrated from cache or finished paging in the target tournament, then
    // just clear the marker - no longer re-centering the view on it
    // (el.scrollIntoView used to do that here). Whatever scroll position
    // the browser's own back-navigation already restored is left alone now;
    // forcing an extra jump back onto the exact card on top of that read as
    // the list visibly hopping around right after the page painted. Opening
    // a tournament (goToTournament below, which is what actually sets this
    // marker) is untouched - this only drops the closing/back side's own
    // re-scroll.
    const [restoreTargetId] = useState(() => sessionStorage.getItem("tournamentsScrollTarget"));
    const [restoreDone, setRestoreDone] = useState(!restoreTargetId);

    useLayoutEffect(() => {
        if (!restoreTargetId || restoreDone) return;
        const el = document.querySelector(`[data-tournament-id="${restoreTargetId}"]`);
        if (el) {
            setRestoreDone(true);
            sessionStorage.removeItem("tournamentsScrollTarget");
        }
    }, [tournaments, restoreTargetId, restoreDone]);

    // Set in changeCurrentPage whenever it actually does the today-split
    // (plain month browsing, no search/filter chips, today inside the
    // range) - the anchor-to-"this week" effect below only applies then,
    // not while searching/filtering (where a result landing in the real
    // calendar's current week would otherwise still carry isCurrentWeek and
    // incorrectly trigger it).
    const futureSplitRef = useRef(false);
    // Whether that anchor has already fired for the current browsed
    // range/filters - reset alongside futureSplitRef on every
    // changeCurrentPage call so a fresh month/filter gets its own anchor,
    // but not re-triggered by every later tournaments update (bottom-
    // sentinel paging further into the past, a live edit) once it's done
    // its one job for this view.
    const anchoredRef = useRef(false);

    // bumped on every filter/date reset so a late-arriving response from a
    // superseded request (e.g. user flips through months faster than the
    // network replies) can be told apart from the request that matches the
    // current filters, and gets ignored instead of clobbering fresh state
    // with stale data - see getTournaments below
    const requestIdRef = React.useRef(0);

    // "YYYY-MM-DD", local calendar day - same formula as formatYMD below,
    // duplicated here because changeCurrentPage (which needs it) is defined
    // above that declaration in the file.
    const toYMD = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const changeCurrentPage = (start_date, end_date, chips = activeChips) => {
        requestIdRef.current += 1;

        // Plain month browsing (no search/filter chips) with today actually
        // inside the browsed range: split the month at "this week" instead
        // of fetching start_date..end_date in one go. The past side (today
        // backward to the start of the month) becomes the main/initial
        // query - with the backend's default -start_date ordering, its
        // first page already lands on "this week" directly, so the very
        // first paint is already at the right spot. The future side (today
        // forward to the end of the month) is held back as `futureCursor`
        // and fetched eagerly in the background right after (see
        // fetchAllFuture below) rather than on a scroll gesture - once it
        // lands above "this week", the anchor effect below scrolls back
        // down to keep the view pinned there instead of on whatever future
        // week ended up on top.
        let pastEndDate = end_date;
        let futureUrl = null;
        if (chips.length === 0 && start_date && end_date) {
            const todayStr = toYMD(new Date());
            if (start_date <= todayStr && todayStr <= end_date) {
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                const dow = (now.getDay() + 6) % 7; // 0 = Monday
                const monday = new Date(now);
                monday.setDate(now.getDate() - dow);
                const sunday = new Date(monday);
                sunday.setDate(monday.getDate() + 6);
                const sundayStr = toYMD(sunday);
                pastEndDate = sundayStr < end_date ? sundayStr : end_date;

                const futureStart = new Date(sunday);
                futureStart.setDate(sunday.getDate() + 1);
                const futureStartStr = toYMD(futureStart);
                if (futureStartStr <= end_date) {
                    const futureParams = new URLSearchParams();
                    futureParams.append("start_date", futureStartStr);
                    futureParams.append("end_date", end_date);
                    // ascending, closest-to-now first - a continuous scroll
                    // up should reveal "next week" before "the last week of
                    // the month", not the other way around.
                    futureParams.append("ordering", "start_date");
                    if (!is_authorized || isSuperAdmin) futureParams.append("all", "true");
                    if (compactTable) futureParams.append("limit", Math.max(10, Math.ceil(window.innerHeight / 40) + 5));
                    futureUrl = `/api/v1.0/tournaments/?${futureParams.toString()}`;
                }
            }
        }
        futureSplitRef.current = !!futureUrl;
        anchoredRef.current = false;

        const params = new URLSearchParams();
        // a text/filter search should look across all tournaments, not just
        // the currently browsed month - only scope by date when the user
        // isn't searching (chips.length === 0, i.e. plain month browsing)
        if (chips.length === 0) {
            if (start_date) params.append("start_date", start_date);
            if (pastEndDate) params.append("end_date", pastEndDate);
        }

        chips.forEach(chip => {
            params.append(chip.type, chip.realValue);
        });

        if (!is_authorized || isSuperAdmin) {
            params.append("all", "true");
        }
        // See ClubsList.js's own copy of this same formula for the full
        // story: a Compact Table row runs ~40px tall, so the backend's own
        // default page size (sized for cards) leaves a compact list several
        // screens short of a full viewport, forcing extra scroll-triggered
        // fetches just to fill it once.
        if (compactTable) {
            params.append("limit", Math.max(10, Math.ceil(window.innerHeight / 40) + 5));
        }
        setTournaments([]);
        setSkeletonCards(24);
        setHasError(false);
        setFutureCursor(futureUrl);
        setLoadingFuture(false);

        if (chips.length > 0) {
            setCurrentPage(`/api/v1.0/tournaments/search/?${params.toString()}`);
            setIsSearching(true);
        } else {
            setCurrentPage(`/api/v1.0/tournaments/${params.toString() ? "?" + params.toString() : ""}`);
            setIsSearching(false);
        }
    };

    const isFirstRender = React.useRef(true);
    useEffect(() => {
        document.title = t("Tournaments");
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (tournaments.length === 0) {
                changeCurrentPage(startDate, endDate, activeChips);
            }
            return;
        }
        changeCurrentPage(startDate, endDate, activeChips);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, activeChips]);

    const getTournaments = async () => {
        const requestId = requestIdRef.current;
        setLoader(true);
        try {
            const response = await prefetchUrl(api, currentPage);
            // filters changed again while this was in flight - a newer
            // request already owns the state, so applying this stale
            // response would overwrite it with the wrong date range's data
            if (requestId !== requestIdRef.current) return;

            const results = response.data.results || [];
            const totalCount = response.data.count || 0;

            setTournaments(prev => {
                const existingIds = new Set(prev.map(t => t.id));
                const newItems = results.filter(t => !existingIds.has(t.id));
                return [...prev, ...newItems];
            });
            setCurrentPage(response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, '') : null);

            const updatedCount = tournaments.length + results.length;
            const remaining = totalCount - updatedCount;
            setSkeletonCards(remaining > 0 ? Math.min(24, remaining) : 0);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error(error);
            setHasError(true);
        } finally {
            if (requestId === requestIdRef.current) setLoader(false);
        }
    };

    const handleRetry = () => {
        setHasError(false);
        getTournaments();
    };

    const stillRestoring = !restoreDone;
    useEffect(() => {
        if ((inView || tournaments.length === 0 || stillRestoring) && currentPage && !loader && !hasError) {
            getTournaments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, currentPage, loader, isSearching, stillRestoring]);

    // Eagerly loads every remaining week after "this week" (see
    // changeCurrentPage) in the background, looping through `next` pages
    // until the future side is exhausted, instead of paging it in lazily on
    // a scroll gesture - downward/past paging (the `ref`/`inView` sentinel
    // above) stays lazy, this side doesn't since there's no sentinel that
    // could ever sit above the very first rendered item to drive it.
    // Accumulates locally and only calls setTournaments once at the very
    // end (not per page) so the DOM only grows above the fold once, right
    // before the anchor effect below scrolls back down to "this week" -
    // merging page by page would otherwise visibly push the page down a
    // little more on each intermediate batch.
    const fetchAllFuture = async () => {
        if (!futureCursor || loadingFuture) return;
        const requestId = requestIdRef.current;
        setLoadingFuture(true);
        let url = futureCursor;
        let accumulated = [];
        try {
            while (url) {
                const response = await prefetchUrl(api, url);
                if (requestId !== requestIdRef.current) return;
                accumulated = accumulated.concat(response.data.results || []);
                url = response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, '') : null;
            }
            if (accumulated.length > 0) {
                setTournaments(prev => {
                    const existingIds = new Set(prev.map(t => t.id));
                    const newItems = accumulated.filter(t => !existingIds.has(t.id));
                    return [...prev, ...newItems];
                });
            }
            setFutureCursor(null);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error(error);
            // Secondary/background direction - fail silently (no retry UI)
            // rather than looping forever on a broken request.
            setFutureCursor(null);
        } finally {
            if (requestId === requestIdRef.current) setLoadingFuture(false);
        }
    };

    useEffect(() => {
        if (futureCursor && !loadingFuture) fetchAllFuture();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [futureCursor]);

    // Pins the view on "this week" once the eagerly-loaded future weeks
    // have landed above it - without this, the user would land on whatever
    // now-furthest-future week ended up on top instead of where
    // changeCurrentPage's own "first page already lands on this week"
    // promise meant them to start. useLayoutEffect (not useEffect) so this
    // runs before the browser paints the newly-taller page, rather than
    // visibly snapping back down a frame later. Skipped entirely while
    // restoring to a specific previously-clicked tournament (restoreTargetId
    // truthy - that other effect owns the scroll instead, and this would
    // otherwise fight it); skipped while futureSplitRef is false (searching/
    // filtering, or a month that doesn't contain today - nothing to pin
    // against); runs once per browsed range (anchoredRef, reset alongside
    // futureSplitRef in changeCurrentPage) so later tournaments updates
    // (bottom-sentinel paging further into the past, a live edit) don't
    // keep re-snapping the scroll back up here.
    useLayoutEffect(() => {
        if (!futureSplitRef.current || anchoredRef.current || restoreTargetId) return;
        if (futureCursor || loadingFuture) return;
        const el = document.querySelector('[data-current-week="true"]');
        if (!el) return;
        anchoredRef.current = true;
        el.scrollIntoView({ behavior: "auto", block: "start" });
    }, [tournaments, futureCursor, loadingFuture, restoreTargetId]);

    const formatYMD = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const handlePrevMonth = (e) => {
        e.preventDefault();
        const currentEndDate = endDate || new Date().toISOString().split("T")[0];
        const [year, month] = currentEndDate.split("-");
        const d = new Date(Number(year), Number(month) - 1, 1);
        d.setMonth(d.getMonth() - 1);
        const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        setStartDate(formatYMD(firstDay));
        setEndDate(formatYMD(lastDay));
    };
    const handleNextMonth = (e) => {
        e.preventDefault();
        const currentEndDate = endDate || new Date().toISOString().split("T")[0];
        const [year, month] = currentEndDate.split("-");
        const d = new Date(Number(year), Number(month) - 1, 1);
        d.setMonth(d.getMonth() + 1);
        const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        setStartDate(formatYMD(firstDay));
        setEndDate(formatYMD(lastDay));
    };
    const monthSwitcher = () => {
        const currentEndDate = endDate || new Date().toISOString().split("T")[0];
        const [year, month] = currentEndDate.split("-");
        const d = new Date(Number(year), Number(month) - 1, 1);
        const currentLang = (typeof i18n !== 'undefined' && i18n.language) ? i18n.language : "uk";
        const monthName = d.toLocaleString(currentLang, { month: "long" });
        return (
            <>
                <li>
                    <a href="#" onClick={handlePrevMonth} style={{border: 0, paddingRight: 0}} className="chevron-btn1"><FeatherIcon icon="chevron-left" /></a>
                </li>
                <li
                    style={{position: "relative", background: "transparent"}}
                >
                    <div
                        className="month-name"
                        onClick={(e) => { const input = e.currentTarget.nextSibling; if (input && input.showPicker) { try { input.showPicker(); } catch (err) {} } }}>
                        <a
                            href="#"
                            className="action-button matches-btn"
                        >
                            <FeatherIcon icon="calendar" /> <span className="month-name-title">{t(monthName)} {year}</span>
                        </a>
                    </div>
                    <input type="month" value={`${year}-${month}`} onChange={(e) => { if (!e.target.value) return; const [y, m] = e.target.value.split("-"); const firstDay = new Date(Number(y), Number(m) - 1, 1); const lastDay = new Date(Number(y), Number(m), 0); setStartDate(formatYMD(firstDay)); setEndDate(formatYMD(lastDay)); }} style={{ position: "absolute", width: 0, height: 0, opacity: 0, border: "none", padding: 0, margin: 0, pointerEvents: "none" }} tabIndex={-1} />
                </li>
                <li><a href="#" onClick={handleNextMonth} style={{border: 0, paddingLeft: 0}} className="chevron-btn1"><FeatherIcon icon="chevron-right" /></a></li>
            </>
        );
    };

    const goToTournament = (tournament) => {
        sessionStorage.setItem(listCacheKey, JSON.stringify({
            query: window.location.search,
            tournaments,
            currentPage,
            futureCursor,
            skeletonCards,
            savedAt: Date.now(),
        }));
        sessionStorage.setItem("tournamentsScrollTarget", tournament.id);
        history.push(is_authorized ? `/tournaments/${tournament.id}?admin=1` : `/tournaments/${tournament.id}`);
    };

    const handleCreateTournament = async (e) => {
        e?.preventDefault?.();

        let currentClubs = clubs;
        if (!currentClubs || currentClubs.length < 1) {
            try {
                const response = await api.get("/api/v1.0/clubs/");
                const fetched = response.data.results || response.data || [];
                currentClubs = Array.isArray(fetched) ? fetched : [];
                setClubs(currentClubs);
            } catch (err) {
                console.error(err);
            }
        }

        if (currentClubs && currentClubs.length < 1) {
            toast.warning(t("Create a club first"));
            history.push("/admin/clubs");
            return;
        }

        const today = new Date().toISOString().slice(0, 10);

        try {
            const response = await api.post("/api/v1.0/tournaments/", { start_date: today, end_date: today });
            const tournamentId = response.data.id;
            if (tournamentId) history.push(`/tournaments/${tournamentId}?admin=1`);
        } catch (error) {
            console.error(error);
            const errMsg = error.response?.data?.detail || (typeof error.response?.data === "string" ? error.response.data : null);
            if (error.response?.status === 400 && (errMsg?.toLowerCase().includes("club") || error.response?.data?.club)) {
                toast.warning(t("Create a club first"));
                history.push("/admin/clubs");
            } else {
                toast.error(errMsg || t("Something went wrong!"));
            }
        }
    };

    const formatTitle = (text) => {
        if (!text) return "";
        return text.toLowerCase().split(" ").map(word => word.split("-").map(part => part.charAt(0).toUpperCase() + part.slice(1)).join("-")).join(" ");
    };

    const handleDeleteTournament = (id) => {
        setTournaments(prev => prev.filter(t => t.id !== id));
    };

    const addDays = (date, n) => {
        const d = new Date(date);
        d.setDate(d.getDate() + n);
        return d;
    };
    const dateKey = (date) => {
        const d = parseDate(date);
        if (!d) return "";
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };
    const tournamentsCountLabel = (count) => {
        const mod10 = count % 10;
        const mod100 = count % 100;
        let key = "tournament_many";
        if (mod10 === 1 && mod100 !== 11) key = "tournament_one";
        else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) key = "tournament_few";
        return `${count} ${t(key)}`;
    };
    const getMonday = (date) => {
        const d = parseDate(date) || new Date();
        d.setHours(0, 0, 0, 0);
        const day = (d.getDay() + 6) % 7;
        d.setDate(d.getDate() - day);
        return d;
    };
    const formatWeekRange = (monday, sunday) => {
        const sameMonth = monday.getMonth() === sunday.getMonth();
        const monthName = (d) => d.toLocaleDateString(i18n.language, { month: "long" });
        return sameMonth
            ? `${monday.getDate()}–${sunday.getDate()} ${monthName(sunday)}`
            : `${monday.getDate()} ${monthName(monday)} – ${sunday.getDate()} ${monthName(sunday)}`;
    };
    const formatDayLabel = (day) =>
        day.toLocaleDateString(i18n.language, { day: "numeric", month: "long", weekday: "short" });

    // Two-level grouping: weeks on the outside (Last/This/Next week, same as
    // before), each week's own items further split into day sub-groups
    // (Yesterday/Today/Tomorrow, else a plain date) - so a card feed still
    // reads as "this week" at a glance, but doesn't lump every day of it
    // into one undifferentiated pile.
    const weekGroups = useMemo(() => {
        const todayMonday = getMonday(new Date());
        const todayWeekKey = dateKey(todayMonday);
        const lastWeekKey = dateKey(addDays(todayMonday, -7));
        const nextWeekKey = dateKey(addDays(todayMonday, 7));

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayDayKey = dateKey(today);
        const yesterdayDayKey = dateKey(addDays(today, -1));
        const tomorrowDayKey = dateKey(addDays(today, 1));

        // weeks near a month boundary can dip into the next/previous month,
        // but the tournaments themselves shouldn't - only keep ones whose
        // own date range actually overlaps the selected month (so a
        // multi-day tournament crossing the boundary still shows, but one
        // that's fully in the other month doesn't just because its week does).
        // None of this applies while searching - search results span all
        // time on purpose, so skip both the month filter and the clamp.
        let monthStart = !isSearching && startDate ? parseDate(startDate) : null;
        let monthEnd = !isSearching && endDate ? parseDate(endDate) : null;
        if (monthStart) monthStart.setHours(0, 0, 0, 0);
        if (monthEnd) monthEnd.setHours(23, 59, 59, 999);

        // when "next week" spans into the following calendar month, the plain
        // month-end cutoff below hides it entirely while browsing today's
        // month - extend the cutoff to cover the full next week so it still
        // shows up. Same idea on the start side for "last week" dipping into
        // the previous month.
        if (monthStart && monthEnd && today >= monthStart && today <= monthEnd) {
            const nextWeekEnd = addDays(todayMonday, 13);
            nextWeekEnd.setHours(23, 59, 59, 999);
            if (nextWeekEnd > monthEnd) monthEnd = nextWeekEnd;

            const lastWeekStart = addDays(todayMonday, -7);
            lastWeekStart.setHours(0, 0, 0, 0);
            if (lastWeekStart < monthStart) monthStart = lastWeekStart;
        }

        const inSelectedMonth = (tournament) => {
            if (!monthStart || !monthEnd || !tournament.start_date) return true;
            const tStart = parseDate(tournament.start_date);
            if (!tStart) return true;
            const tEnd = tournament.end_date ? (parseDate(tournament.end_date) || tStart) : tStart;
            return tEnd >= monthStart && tStart <= monthEnd;
        };

        // a tournament running for a month or half a year started long
        // before this view's range - grouping it by its real start_date
        // would pin it to some distant week (with a date label from a
        // different month entirely). Clamp the date used for grouping to
        // the visible month, so it lands in the first week it's actually
        // visible in here instead.
        const effectiveDate = (tournament) => {
            const tStart = parseDate(tournament.start_date);
            if (!tStart) return new Date(0);
            return monthStart && tStart < monthStart ? monthStart : tStart;
        };

        // future weeks on top, this week in the middle, past weeks at the
        // bottom - i.e. newest/furthest-future date first, descending down
        // to the oldest/furthest-past date
        const sorted = [...tournaments].filter(inSelectedMonth).sort((a, b) => {
            if (!a.start_date) return 1;
            if (!b.start_date) return -1;
            return effectiveDate(b) - effectiveDate(a);
        });

        const weeks = [];
        sorted.forEach(tournament => {
            if (!tournament.start_date) {
                const last = weeks[weeks.length - 1];
                if (last && !last.monday) {
                    last.items.push(tournament);
                } else {
                    weeks.push({ key: `unknown-${weeks.length}`, monday: null, items: [tournament] });
                }
                return;
            }
            const monday = getMonday(effectiveDate(tournament));
            const key = dateKey(monday);
            const last = weeks[weeks.length - 1];
            if (last && last.key === key) {
                last.items.push(tournament);
                return;
            }
            let label = null;
            if (key === todayWeekKey) label = t("This week");
            else if (key === lastWeekKey) label = t("Last week");
            else if (key === nextWeekKey) label = t("Next week");
            weeks.push({ key, monday, sunday: addDays(monday, 6), label, isCurrentWeek: key === todayWeekKey, items: [tournament] });
        });

        // Sub-group a week's own items by day, in the same already-sorted
        // order - a multi-day tournament (e.g. a 2-day weekend event) is
        // only grouped under the day it starts, not repeated under every
        // day it spans.
        const groupByDay = (items) => {
            const days = [];
            items.forEach(tournament => {
                if (!tournament.start_date) {
                    const last = days[days.length - 1];
                    if (last && !last.day) {
                        last.items.push(tournament);
                    } else {
                        days.push({ key: `unknown-day-${days.length}`, day: null, items: [tournament] });
                    }
                    return;
                }
                const day = new Date(effectiveDate(tournament));
                day.setHours(0, 0, 0, 0);
                const key = dateKey(day);
                const last = days[days.length - 1];
                if (last && last.key === key) {
                    last.items.push(tournament);
                    return;
                }
                let label = null;
                if (key === todayDayKey) label = t("Today");
                else if (key === yesterdayDayKey) label = t("Yesterday");
                else if (key === tomorrowDayKey) label = t("Tomorrow");
                // Today/Yesterday/Tomorrow alone didn't say which actual date
                // that was - append the same weekday/day/month bit the plain
                // date label already uses, so it reads e.g. "Сьогодні, вт, 22
                // вересня" instead of just "Сьогодні".
                if (label) label = `${label}, ${formatDayLabel(day)}`;
                days.push({ key, day, label, items: [tournament] });
            });
            return days;
        };

        // Every week gets split into day sub-groups, same as "this week" -
        // Yesterday/Today/Tomorrow labels only apply within their own week
        // (groupByDay compares against the real calendar day), other days
        // just get a plain date label via formatDayLabel.
        return weeks.map(week => ({
            ...week,
            days: groupByDay(week.items)
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tournaments, i18n.language, startDate, endDate, isSearching]);

    // Driven directly by toolbarProgress (0=expanded, 1=collapsed), not the
    // .compact CSS class + transition - see useCompactToolbar's own comment
    // for why a timed transition can't track a continuous scroll gesture
    // 1:1 (it visibly lands out of step with wherever the scroll already
    // got to by the time it finishes). No transition here at all: every
    // scroll frame recomputes these directly off scroll position instead.
    // With an active search (a picked chip, or results already in from a
    // typed query) the row stays fully expanded regardless of scroll,
    // instead of the Telegram-style auto-collapse-to-a-header-icon below -
    // same "stays pinned once there's something to show" behavior the
    // tournament page's own search bar already has (Tournament.js,
    // position:sticky while searchOpen, no scroll-driven collapse at all).
    // Collapsing an active search away mid-scroll hid the very chips/query
    // the user was still acting on, forcing a trip to the header icon just
    // to see or edit them.
    const hasActiveSearch = activeChips.length > 0 || isSearching;
    const effectiveToolbarProgress = hasActiveSearch ? 0 : toolbarProgress;

    const toolbarStyle = {
        paddingTop: `${5 * (1 - effectiveToolbarProgress)}px`,
        paddingBottom: toolbarActivated ? `${10 * (1 - effectiveToolbarProgress)}px` : "10px"
    };
    const searchRowStyle = {
        paddingBottom: 0,
        maxHeight: `${60 * (1 - effectiveToolbarProgress)}px`,
        opacity: 1 - effectiveToolbarProgress,
        pointerEvents: effectiveToolbarProgress >= 0.99 ? "none" : "auto"
    };

    // Zebra striping index for the Compact Table rows below - a plain
    // running counter (not CSS nth-child) because week/day divider elements
    // sit between rows in the same list, which would throw nth-child's own
    // count off. Reset each render; mutated in place while rendering rows.
    let compactRowIndex = 0;

    return (
        <Container className="page_container">
            <div ref={toolbarSentinelRef} style={{ height: "1px" }} />
            {/* Only the search row lives in the sticky/collapsing wrapper - it's
                the one that hands off to the compact header search icon (see
                useCompactToolbar). The month/view-toggle row below sits outside
                it, in normal flow, so it scrolls away with the page instead of
                staying pinned under the header alongside the search bar. */}
            <div className="sticky-toolbar" style={toolbarStyle}>
                <nav className="tabs-container toolbar-search-row" style={searchRowStyle}>
                    <ul className="tabs">
                        <li style={{width: "100%", minWidth: 0}}>
                            <TournamentSearch
                                ref={searchInputRef}
                                activeChips={activeChips}
                                onChange={(chips) => { activeChipsRef.current = chips; setActiveChips(chips); }}
                                is_authorized={is_authorized}
                                authorizedClubs={clubs}
                                onInputValueChange={(val) => { searchQueryRef.current = val; }}
                                onBlur={() => { if (activeChipsRef.current.length === 0 && !searchQueryRef.current.trim()) collapseToolbar(); }}
                            />
                        </li>
                    </ul>
                </nav>
            </div>

            <div className="tabs-container toolbar-top-row">
                <ul className="tabs">
                    {!isSearching && monthSwitcher()}
                    {/* Compact Table is a global override (ThemeContext's compactTable,
                        not this page's own view_mode cookie) - while it's on, Card/Grid
                        has nothing to switch between, so the toggle just goes away
                        instead of sitting there disabled. */}
                    {!isMobileWidth && !compactTable && (
                        <IconDropdown
                            activeId={viewMode}
                            onChange={toggleViewMode}
                            options={[
                                { id: "grid", icon: "grid", label: "Grid View" },
                                { id: "cards", icon: "credit-card", label: "Card View" }
                            ]}
                        />
                    )}
                </ul>
            </div>

            {compactTable ? (
                <div className="trows">
                    {weekGroups.map((week) => (
                        <React.Fragment key={week.key}>
                            {week.monday && (
                                <div className="week-divider" data-current-week={week.isCurrentWeek ? "true" : undefined}>
                                    <span className="week-divider-range">{week.label || formatWeekRange(week.monday, week.sunday)}</span>
                                    <span className="week-divider-count">{tournamentsCountLabel(week.items.length)}</span>
                                </div>
                            )}
                            {week.days.map((group) => (
                                <React.Fragment key={group.key}>
                                    {group.day && (
                                        <div className="day-divider">
                                            <span className="day-divider-label">{group.label || formatDayLabel(group.day)}</span>
                                            <span className="day-divider-count">{tournamentsCountLabel(group.items.length)}</span>
                                        </div>
                                    )}
                                    {group.items.map((tournament, idx) => {
                                        const locationValue = [tournament.city, tournament.location].filter(Boolean).join(", ");
                                        const playersValue = tournament.players != null
                                            ? `${tournament.players}${tournament.player_quantity != null ? `/${tournament.player_quantity}` : ""}`
                                            : "—";
                                        const zebra = compactRowIndex++ % 2 === 1;
                                        return (
                                            <CompactTableRow
                                                key={tournament.id || `${group.key}-${idx}`}
                                                variant="tournaments"
                                                zebra={zebra}
                                                data-tournament-id={tournament.id}
                                                statusDotClass={getStatusClass(tournament)}
                                                avatarSrc={tournament.club_logo || tournament.thumbnail || tournament.image || tournament.location_image}
                                                title={formatTitle(tournament.name)}
                                                onClick={() => goToTournament(tournament)}
                                                stats={[
                                                    { icon: "calendar", value: tournament.date_range || "—" },
                                                    { icon: "map-pin", value: locationValue || "—", className: "stat-location" },
                                                    { icon: "users", value: playersValue }
                                                ]}
                                                actions={is_authorized && (
                                                    <TournamentMenuList tournamentId={tournament.id} onDeleted={handleDeleteTournament} asModal title={formatTitle(tournament.name)} />
                                                )}
                                            />
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}
                    {(loader || (currentPage && tournaments.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <div className="trow trow-tournaments" key={`skel-trow-${idx}`} style={{ pointerEvents: "none" }}>
                            <span />
                            <Skeleton circle width={20} height={20} />
                            <Skeleton width="60%" height={12} />
                            <Skeleton width="90%" height={11} />
                            <Skeleton width="90%" height={11} />
                            <Skeleton width="90%" height={11} />
                            <span />
                        </div>
                    ))}
                </div>
            ) : effectiveViewMode !== "grid" ? (
                <div className="tournament-cards">
                    {weekGroups.map((week) => (
                        <React.Fragment key={week.key}>
                            {week.monday && (
                                <div className="week-divider" data-current-week={week.isCurrentWeek ? "true" : undefined}>
                                    <span className="week-divider-range">{week.label || formatWeekRange(week.monday, week.sunday)}</span>
                                    <span className="week-divider-count">{tournamentsCountLabel(week.items.length)}</span>
                                </div>
                            )}
                            {week.days.map((group) => (
                            <React.Fragment key={group.key}>
                            {group.day && (
                                <div className="day-divider">
                                    <span className="day-divider-label">{group.label || formatDayLabel(group.day)}</span>
                                    <span className="day-divider-count">{tournamentsCountLabel(group.items.length)}</span>
                                </div>
                            )}
                            {group.items.map((tournament, idx) => (
                                <div
                                    className="tournament-card"
                                    key={tournament.id || `${group.key}-${idx}`}
                                    data-tournament-id={tournament.id}
                                    onClick={() => goToTournament(tournament)}
                                >
                                    <div className={`tournament-card-date ${getStatusClass(tournament)}`}>{tournament.date_range}</div>
                                    {is_authorized && (
                                        <div className="tournament-card-actions" onClick={e => e.stopPropagation()}>
                                            <TournamentMenuList tournamentId={tournament.id} onDeleted={handleDeleteTournament} asModal title={formatTitle(tournament.name)} />
                                        </div>
                                    )}
                                    <div className="tournament-card-top">
                                        <div className="tournament-card-thumb">
                                            {(() => {
                                                const thumbSrc = tournament.club_logo || tournament.thumbnail || tournament.image || tournament.location_image;
                                                if (thumbSrc) return <img src={thumbSrc} alt="" />;
                                                const label = tournament.club || tournament.name;
                                                return label
                                                    ? <span style={{ fontSize: "16px", fontWeight: 600 }}>{getInitials(label)}</span>
                                                    : <FeatherIcon icon="camera-off" />;
                                            })()}
                                        </div>
                                        <div className="tournament-card-titles">
                                            <div className="tournament-card-title">{formatTitle(tournament.name)}</div>
                                            <div className="tournament-card-club">
                                                {tournament.club && (
                                                    <>
                                                        <span className="tournament-card-club-text">{tournament.club}</span>
                                                        {clubIdByName[tournament.club] && (
                                                            <a
                                                                href="#"
                                                                onClick={e => openClubModal(e, clubIdByName[tournament.club])}
                                                                className="tournament-card-club-link"
                                                                title={t("Club page")}
                                                            >
                                                                <FeatherIcon icon="external-link" size={10} />
                                                            </a>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            <div className="tournament-card-info">
                                                {tournament.city}
                                                {tournament.city && tournament.location && " · "}
                                                {tournament.location && (
                                                    <span className="tournament-card-location">
                                                        <span className="tournament-card-location-text">{tournament.location}</span>
                                                    </span>
                                                )}
                                                {(tournament.city || tournament.location) && tournament.players != null && " · "}
                                                {tournament.players != null && (
                                                    <span className="tournament-card-players">
                                                        <FeatherIcon icon="users" size={11} />
                                                        {tournament.players}{tournament.player_quantity != null && `/${tournament.player_quantity}`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <details className="tournament-card-details">
                                        <summary className="tournament-card-toggle" onClick={e => e.stopPropagation()}>
                                            <svg className="tournament-card-toggle-arrow" height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                                <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
                                            </svg>
                                        </summary>
                                        <div className="tournament-card-meta">
                                            <table className="tournament_table">
                                                <tbody>
                                                {(() => {
                                                    const status = getTournamentStatus(tournament);
                                                    const statusClass = status === "live" ? "in-progress" : status === "completed" ? "completed" : "expected";
                                                    const statusIcon = statusClass === "in-progress" ? "activity" : statusClass === "completed" ? "check" : "clock";
                                                    const statusLabel = status === "live" ? t("In progress") : status === "completed" ? t("Completed") : t("Expected");
                                                    return (
                                                        <tr>
                                                            <td>{t("Status")}</td>
                                                            <td>
                                                                <span className={`t-status-badge-sm inline ${statusClass}`}>
                                                                    <FeatherIcon icon={statusIcon} />{statusLabel}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })()}
                                                <tr><td>{t("Name")}</td><td>{formatTitle(tournament.name)}</td></tr>
                                                {/* TODO: /api/v1.0/tournaments/ only returns the club's name, not its
                                                    id, so this can't open ClubProfileModal (?club_id=...) the way the
                                                    tournament header's club link does - needs club_id added to this
                                                    endpoint's response first. */}
                                                <tr><td>{t("Club")}</td><td>{tournament.club}</td></tr>
                                                <tr><td>{t("Date")}</td><td>{tournament.days?.length > 0 ? tournament.days.map(day => t(`days_short.${day}`)).join("-") + " · " : ""}{tournament.date_range}</td></tr>
                                                <tr><td>{t("Location")}</td><td>{tournament.location} · {tournament.city}{tournament.city && tournament.country && ", "}{t(getOptionName(COUNTRY_OPTIONS, tournament.country))}</td></tr>
                                                <tr><td>{t("Format")}</td><td>{getGradeName(tournament.grade)} ·&nbsp;{tournament.gender && ((tournament.gender === 3 && tournament.grade === 1) ? t("Admixed") : t(`gender.${tournament.gender}`))}</td></tr>
                                                <tr><td>{t("Level")}</td><td>{ tournament.format }</td></tr>
                                                <tr><td>{t("Players")}</td><td style={{textAlign: "right"}}><div style={{position: "relative", height: "30px"}}><div className="tournament-players" style={{ color: "var(--primary-color) !important", position: "absolute", right: 0, top: "-5px"}}>{tournament.players}</div></div></td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </details>
                                </div>
                            ))}
                            </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}
                    {(loader || (currentPage && tournaments.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <div className="tournament-card" key={`skel-card-${idx}`} style={{ pointerEvents: 'none' }}>
                            <div className="tournament-card-date" style={{ border: 'none', background: 'transparent', padding: 0 }}>
                                <Skeleton width={56} height={16} borderRadius={20} />
                            </div>
                            <div className="tournament-card-top">
                                <div className="tournament-card-thumb" style={{ background: 'transparent' }}>
                                    <Skeleton circle width={52} height={52} />
                                </div>
                                <div className="tournament-card-titles" style={{ flex: 1, minWidth: 0 }}>
                                    <div className="tournament-card-title"><Skeleton width="65%" height={16} /></div>
                                    <div className="tournament-card-club"><Skeleton width="40%" height={13} style={{ marginTop: 4 }} /></div>
                                    <div className="tournament-card-info"><Skeleton width="50%" height={11} style={{ marginTop: 4 }} /></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <ResponsiveMasonry columnsCountBreakPoints={{ 0: 1, 600: 2, 900: 3, 1200: 4, 1500: 5 }}>
                    <Masonry gutter="10px">
                        {tournaments.map((tournament, idx) => (
                            <div
                                className="tournament-card tournament-grid-card"
                                key={tournament.id || idx}
                                data-tournament-id={tournament.id}
                                onClick={() => goToTournament(tournament)}
                            >
                                <div className="tournament-grid-card-photo">
                                    <PreImage
                                        src={tournament.thumbnail || tournament.image || tournament.location_image || tournament.club_logo || "no"}
                                        width={tournament.width || "800"}
                                        height={tournament.height || "530"}
                                        fallbackLabel={tournament.club || tournament.name}
                                    />
                                </div>
                                <div className="tournament-grid-card-body">
                                    <div className={`tournament-card-date ${getStatusClass(tournament)}`}>{tournament.date_range}</div>
                                    {is_authorized && (
                                        <div className="tournament-card-actions" onClick={e => e.stopPropagation()}>
                                            <TournamentMenuList tournamentId={tournament.id} onDeleted={handleDeleteTournament} asModal title={formatTitle(tournament.name)} />
                                        </div>
                                    )}
                                    <div className="tournament-card-top">
                                        <div className="tournament-card-titles">
                                            <div className="tournament-card-title">{formatTitle(tournament.name)}</div>
                                            <div className="tournament-card-club">
                                                {tournament.club && (
                                                    <>
                                                        <span className="tournament-card-club-text">{tournament.club}</span>
                                                        {clubIdByName[tournament.club] && (
                                                            <a
                                                                href="#"
                                                                onClick={e => openClubModal(e, clubIdByName[tournament.club])}
                                                                className="tournament-card-club-link"
                                                                title={t("Club page")}
                                                            >
                                                                <FeatherIcon icon="external-link" size={10} />
                                                            </a>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            <div className="tournament-card-info">
                                                {tournament.city}
                                                {tournament.city && tournament.location && " · "}
                                                {tournament.location && (
                                                    <span className="tournament-card-location">
                                                        <span className="tournament-card-location-text">{tournament.location}</span>
                                                    </span>
                                                )}
                                                {(tournament.city || tournament.location) && tournament.players != null && " · "}
                                                {tournament.players != null && (
                                                    <span className="tournament-card-players">
                                                        <FeatherIcon icon="users" size={11} />
                                                        {tournament.players}{tournament.player_quantity != null && `/${tournament.player_quantity}`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <details className="tournament-card-details">
                                        <summary className="tournament-card-toggle" onClick={e => e.stopPropagation()}>
                                            <svg className="tournament-card-toggle-arrow" height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                                <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
                                            </svg>
                                        </summary>
                                        <div className="tournament-card-meta">
                                            <table className="tournament_table">
                                                <tbody>
                                                {(() => {
                                                    const status = getTournamentStatus(tournament);
                                                    const statusClass = status === "live" ? "in-progress" : status === "completed" ? "completed" : "expected";
                                                    const statusIcon = statusClass === "in-progress" ? "activity" : statusClass === "completed" ? "check" : "clock";
                                                    const statusLabel = status === "live" ? t("In progress") : status === "completed" ? t("Completed") : t("Expected");
                                                    return (
                                                        <tr>
                                                            <td>{t("Status")}</td>
                                                            <td>
                                                                <span className={`t-status-badge-sm inline ${statusClass}`}>
                                                                    <FeatherIcon icon={statusIcon} />{statusLabel}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })()}
                                                <tr><td>{t("Name")}</td><td>{formatTitle(tournament.name)}</td></tr>
                                                {/* TODO: /api/v1.0/tournaments/ only returns the club's name, not its
                                                    id, so this can't open ClubProfileModal (?club_id=...) the way the
                                                    tournament header's club link does - needs club_id added to this
                                                    endpoint's response first. */}
                                                <tr><td>{t("Club")}</td><td>{tournament.club}</td></tr>
                                                <tr><td>{t("Date")}</td><td>{tournament.days?.length > 0 ? tournament.days.map(day => t(`days_short.${day}`)).join("-") + " · " : ""}{tournament.date_range}</td></tr>
                                                <tr><td>{t("Location")}</td><td>{tournament.location} · {tournament.city}{tournament.city && tournament.country && ", "}{t(getOptionName(COUNTRY_OPTIONS, tournament.country))}</td></tr>
                                                <tr><td>{t("Format")}</td><td>{getGradeName(tournament.grade)} ·&nbsp;{tournament.gender && ((tournament.gender === 3 && tournament.grade === 1) ? t("Admixed") : t(`gender.${tournament.gender}`))}</td></tr>
                                                <tr><td>{t("Level")}</td><td>{ tournament.format }</td></tr>
                                                <tr><td>{t("Players")}</td><td style={{textAlign: "right"}}><div style={{position: "relative", height: "30px"}}><div className="tournament-players" style={{ color: "var(--primary-color) !important", position: "absolute", right: 0, top: "-5px"}}>{tournament.players}</div></div></td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </details>
                                </div>
                            </div>
                        ))}
                        {(loader || (currentPage && tournaments.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                            <div className="tournament-card tournament-grid-card" key={`skel-grid-${idx}`} style={{ pointerEvents: 'none' }}>
                                <div className="tournament-grid-card-photo" style={{ height: 140, background: 'transparent' }}>
                                    <Skeleton height={140} borderRadius={6} />
                                </div>
                                <div className="tournament-grid-card-body">
                                    <div className="tournament-card-date" style={{ border: 'none', background: 'transparent', padding: 0 }}>
                                        <Skeleton width={56} height={16} borderRadius={20} />
                                    </div>
                                    <div className="tournament-card-top">
                                        <div className="tournament-card-titles" style={{ width: '100%' }}>
                                            <div className="tournament-card-title"><Skeleton width="75%" height={16} /></div>
                                            <div className="tournament-card-club"><Skeleton width="50%" height={13} style={{ marginTop: 4 }} /></div>
                                            <div className="tournament-card-info"><Skeleton width="60%" height={11} style={{ marginTop: 4 }} /></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Masonry>
                </ResponsiveMasonry>
            )}

            {!loader && !loadingFuture && tournaments.length === 0 && currentPage === null && futureCursor === null && (
                <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>
                    {t("No tournaments in this period")}
                </p>
            )}

            {hasError && (
                <div style={{ padding: "20px", textAlign: "center" }}>
                    <p style={{ color: "var(--danger-color, #e53935)" }}>{t("Failed to load tournaments")}</p>
                    <button className="action-button" onClick={handleRetry} style={{ margin: "0 auto" }}>{t("Retry")}</button>
                </div>
            )}

            <div ref={ref} style={{ height: "20px", width: "100%" }}></div>
        </Container>
    );
};

export default TournamentsList;
